import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { pushIfNotExists } from "@fidget/common/utils/arrayHelpers";
import { notEmpty } from "@fidget/common/utils/not-empty";
import { isEmpty, size, uniq } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { useStore } from "../../../models";
import { useAgentServices } from "../../../models/agentServices";
import { Capacity } from "../../forms/capacityPicker/capacityPicker";
import { CAPACITY_OPTIONS, mapLimitsToCapacities } from "../../forms/capacityPicker/utils";
import {
  ListActions,
  ListActionsSection,
  ListAddDeleteAgentGroupAction,
  ListDeleteAction,
  ListRoleAction,
  ListTagTagGroupAction
} from "../listActions";
import { ListAgentCapacityAction } from "../listActions/listAgentCapacityAction";
import { useBooleanMap } from "../models/useBooleanMap";
import { SettingsForm } from "../settingsForm";
import { DeleteConfirmationToastOnError } from "../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormExpandIcon } from "../settingsForms/elementForm/icons/elementFormExpandIcon";
import { BooleanListFilter } from "../settingsForms/filters/booleanListFilter";
import { ListFilterWrapper } from "../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { CapacityFilter } from "../settingsForms/filters/pickerFilters/capacityFilter";
import { RoleFilter } from "../settingsForms/filters/pickerFilters/roleFilter";
import { StatusFilter } from "../settingsForms/filters/pickerFilters/statusFilter";
import { TagFilter } from "../settingsForms/filters/pickerFilters/tagFilter";
import { TagGroupFilter } from "../settingsForms/filters/pickerFilters/tagGroupFilter";
import { useSettingsListFilters } from "../settingsForms/filters/useListFilter/useListFilter";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { VerticalSeparator } from "../settingsForms/separators/verticalSeparator/verticalSeparator";
import { AgentBox } from "./components/agentBox/agentBox";
import { PendingAgentBox } from "./components/agentBox/pendingAgentBox";
import { InviteAgentBox } from "./components/inviteAgentBox/inviteAgentBox";
import styles from "./components/styles.css";
import { activeAdviserFilterId, ExistingAdviserForm, inactiveAdviserFilterId } from "./types";
import { useUsersSettings } from "./useUsersSettings";
import { copyExistingAdviserForm, filterInputPredicate } from "./utils/utils";

export const UsersSettingsList = observer(() => {
  const { updatePersistent, activeFilters } = useSettingsListFilters<ExistingAdviserForm>();
  const { items: expandedItems, setItem: setExpanded } = useBooleanMap();
  const {
    currentUserStore: { id: ownUserId, orgConfig },
    agentGroupStore,
    uiStore
  } = useStore();
  const { spinnerService } = useAgentServices();
  const areCallsDisabled = !!orgConfig?.callsDisabled;

  const {
    newItem,
    handleCreateNewClick,
    saveNewItem,
    invitees,
    removeInvitee,
    revokeInvitee,
    removeInviteeFromUi,

    items,
    selectedItems,
    setItemsSelected,
    filteredItems,
    activeFiltersNumber,
    setItemSelected,
    deleteAllSelected,
    forEachSelected,
    getAllSelected,
    uiRemoveExistingItem,
    apiDeleteItem,
    updateItem,

    editedItems,
    onExistingEditClick,
    onSubmitAll,
    onDiscardAll,
    addAdvisersToGroup,
    removeAdvisersFromGroup
  } = useUsersSettings();

  const shouldDisplayInvitees =
    (uiStore.canDisplayAdvisersConfiguration() && activeFiltersNumber === 0) ||
    (activeFiltersNumber === 1 && activeFilters[activeAdviserFilterId]);

  useEffect(() => {
    updatePersistent({
      [activeAdviserFilterId]: {
        filterId: activeAdviserFilterId,
        antagonistId: inactiveAdviserFilterId,
        predicate: (adviser) => adviser.$.isActive.$
      }
    });
  }, []);

  const renderInviteSection = () => {
    return newItem ? (
      <div>
        <InviteAgentBox
          advisers={newItem}
          onSave={saveNewItem}
          removeFromInvitees={removeInvitee}
        />
      </div>
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={"+ " + intl.formatMessage("settings.advisers.inviteNew")}
      />
    );
  };

  const renderRestoreIcon = (adviser: ExistingAdviserForm) => {
    const onClick = async () => {
      try {
        await spinnerService.restoreAgent(adviser.$.id.$);
        const updated = copyExistingAdviserForm(adviser);
        updated.$.isActive.reset(true);
        updateItem(updated);
      } catch (e) {
        logger.error({ message: "Couldn't restore adviser", error: e });
        uiStore.addGenericErrorToast();
      }
    };
    return (
      <p className={styles["restore-text"]} onClick={onClick}>
        {intl.formatMessage("settings.advisers.restore")}
      </p>
    );
  };

  const renderMyself = (adviser: ExistingAdviserForm) => {
    const isExpanded = !!expandedItems[adviser.$.id.$];

    return (
      <ElementForm key={adviser.$.id.$}>
        <ElementFormActions>
          <ElementFormCheckbox
            isSelected={!!selectedItems[adviser.$.id.$]}
            onChange={(val) => setItemSelected(adviser, val)}
            id={adviser.$.id.$}
          />
          <ElementFormExpandIcon
            onClick={() => setExpanded(adviser.$.id.$)}
            isExpanded={isExpanded}
          />
        </ElementFormActions>
        <VerticalSeparator />
        <AgentBox
          form={adviser}
          orgAutoAssignedLimit={orgConfig?.autoAssignedLimit}
          isExpanded={isExpanded}
        />
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormEditIcon onClick={() => onExistingEditClick(adviser)} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderAdviser = (adviser: ExistingAdviserForm) => {
    const isExpanded = !!expandedItems[adviser.$.id.$];

    return (
      <DeleteConfirmationToastOnError
        key={adviser.$.id.$}
        areYouSureText={intl.formatMessage("settings.advisers.areYouSureToRemove")}
        succesfullyDeletedText={intl.formatMessage("settings.advisers.removedSuccessfully")}
        onTimeoutPassed={() => uiRemoveExistingItem(adviser.$.id.$)}
        onDelete={() => apiDeleteItem(adviser.$.id.$)}
        renderComponent={(onDeleteClick) => (
          <ElementForm>
            <ElementFormActions>
              <ElementFormCheckbox
                isSelected={!!selectedItems[adviser.$.id.$]}
                onChange={(val) => setItemSelected(adviser, val)}
                id={adviser.$.id.$}
              />
              <ElementFormExpandIcon
                onClick={() => setExpanded(adviser.$.id.$)}
                isExpanded={isExpanded}
              />
            </ElementFormActions>
            <VerticalSeparator />
            <AgentBox
              form={adviser}
              orgAutoAssignedLimit={orgConfig?.autoAssignedLimit}
              isExpanded={isExpanded}
            />
            <VerticalSeparator />
            {adviser.$.isActive.$ ? (
              <ElementFormActions>
                <ElementFormEditIcon onClick={() => onExistingEditClick(adviser)} />
                {uiStore.canDisplayAdvisersConfiguration() && (
                  <ElementFormDeleteIcon onClick={onDeleteClick} />
                )}
              </ElementFormActions>
            ) : (
              uiStore.canDisplayAdvisersConfiguration() && (
                <ElementFormActions>{renderRestoreIcon(adviser)}</ElementFormActions>
              )
            )}
          </ElementForm>
        )}
      />
    );
  };

  const renderAdvisers = () => (
    <div>
      {filteredItems.map((adviser) => {
        return ownUserId === adviser.$.id.$ ? renderMyself(adviser) : renderAdviser(adviser);
      })}
    </div>
  );

  const renderPendingAdvisers = () => {
    return invitees.map((invitee) => (
      <DeleteConfirmationToastOnError
        areYouSureText={intl.formatMessage("settings.advisers.areYouSureToRemove")}
        succesfullyDeletedText={intl.formatMessage("settings.advisers.removedSuccessfully")}
        onTimeoutPassed={() => removeInviteeFromUi(invitee)}
        onDelete={() => revokeInvitee(invitee)}
        key={invitee.email}
        renderComponent={(onDeleteClick) => (
          <ElementForm>
            <PendingAgentBox {...invitee} />
            {uiStore.canDisplayAdvisersConfiguration() && (
              <>
                <VerticalSeparator />
                <ElementFormActions>
                  <ElementFormDeleteIcon onClick={onDeleteClick} />
                </ElementFormActions>
              </>
            )}
          </ElementForm>
        )}
      />
    ));
  };

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingAdviserForm>>
        <ListFilterWrapper<ExistingAdviserForm>
          placeholder={intl.formatMessage("settings.advisers.filter.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="AdviserInputFilter"
          inputPredicate={filterInputPredicate}
          headerText={intl.formatMessage("settings.advisers")}
        >
          <TagFilter<ExistingAdviserForm>
            predicate={(tag) => (item) => item.$.tagIds.$.includes(tag.id)}
          />
          <TagGroupFilter<ExistingAdviserForm>
            predicate={(tagGroup) => (item) => item.$.tagGroupIds.$.includes(tagGroup.id)}
          />
          <RoleFilter<ExistingAdviserForm>
            predicate={(role) => (item) => item.$.roleId.$ === role.id}
          />
          <StatusFilter<ExistingAdviserForm>
            predicate={(status) => (item) => item.$.presence.$ === status}
            areCallsDisabled={areCallsDisabled}
          />
          <CapacityFilter<ExistingAdviserForm>
            itemsProvider={() =>
              mapLimitsToCapacities(
                uniq([...items.map((i) => i.$.autoAssignedLimit.$), orgConfig?.autoAssignedLimit])
                  .filter(notEmpty)
                  .sort((a, b) => a - b)
              )
            }
            predicate={(capacity) => (item) =>
              item.$.autoAssignedLimit.$ !== undefined
                ? item.$.autoAssignedLimit.$ === capacity
                : orgConfig?.autoAssignedLimit === capacity}
          />
          <BooleanListFilter<ExistingAdviserForm>
            description={intl.formatMessage("settings.advisers.filters.inactive")}
            filterId={inactiveAdviserFilterId}
            predicate={(item) => !item.$.isActive.$}
            antagonistFilterId={activeAdviserFilterId}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected = !!filteredItems.length && size(selectedItems) === size(filteredItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) => setItemsSelected(filteredItems, val)}
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            {uiStore.canDisplayAdvisersAssignments() && (
              <>
                <ListTagTagGroupAction
                  textVariant="adding"
                  text={intl.formatMessage("settings.advisers.action.addSkill")}
                  onTagGroupPicked={(groupId) =>
                    forEachSelected((i) =>
                      i.$.tagGroupIds.onChange(pushIfNotExists(i.$.tagGroupIds.$, groupId))
                    )
                  }
                  onTagPicked={(tagId) =>
                    forEachSelected((i) =>
                      i.$.tagIds.onChange(pushIfNotExists(i.$.tagIds.$, tagId))
                    )
                  }
                />
                <ListTagTagGroupAction
                  textVariant="removing"
                  text={intl.formatMessage("settings.advisers.action.removeSkill")}
                  onTagGroupPicked={(groupId) =>
                    forEachSelected((i) =>
                      i.$.tagGroupIds.onChange(i.$.tagGroupIds.$.filter((v) => groupId !== v))
                    )
                  }
                  onTagPicked={(tagId) =>
                    forEachSelected((i) =>
                      i.$.tagIds.onChange(i.$.tagIds.$.filter((v) => tagId !== v))
                    )
                  }
                />
              </>
            )}

            {uiStore.canDisplayAdvisersConfiguration() && (
              <ListRoleAction
                onPicked={(roleId) => forEachSelected((i) => i.$.roleId.onChange(roleId))}
              />
            )}
            {uiStore.canDisplayAgentGroupsConfiguration() && (
              <ListAddDeleteAgentGroupAction
                values={agentGroupStore.groups}
                onAdded={(group) => {
                  forEachSelected((i) =>
                    i.$.agentGroupIds.onChange(pushIfNotExists(i.$.agentGroupIds.$, group.id))
                  );
                  addAdvisersToGroup(
                    group.id,
                    getAllSelected().map((a) => a.$.id.$)
                  );
                }}
                onDeleted={(group) => {
                  forEachSelected((i) =>
                    i.$.agentGroupIds.onChange(
                      i.$.agentGroupIds.$.filter((groupId) => groupId !== group.id)
                    )
                  );
                  removeAdvisersFromGroup(
                    group.id,
                    getAllSelected().map((a) => a.$.id.$)
                  );
                }}
              />
            )}

            {uiStore.canDisplayAdvisersAutoAssignedLimit() && (
              <ListAgentCapacityAction
                text={intl.formatMessage("settings.advisers.action.capacity")}
                onChange={(capacity: Capacity) => {
                  forEachSelected((i) => i.$.autoAssignedLimit.onChange(capacity.value));
                }}
                itemsProvider={() => [
                  {
                    name: intl.formatMessage("capacityPicker.turnOffIndividualLimit"),
                    value: undefined
                  },
                  {
                    name: intl.formatMessage("capacityPicker.turnOffAutoAssignment"),
                    value: 0
                  },
                  ...CAPACITY_OPTIONS
                ]}
              />
            )}
          </ListActionsSection>
          {uiStore.canDisplayAdvisersConfiguration() && (
            <>
              <VerticalSeparator />
              <ListDeleteAction onDelete={deleteAllSelected} />
            </>
          )}
        </ListActions>
      </SettingsForm>
    );
  };

  return (
    <>
      {uiStore.canDisplayAdvisersConfiguration() && renderInviteSection()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderAdvisers()}
      {shouldDisplayInvitees && renderPendingAdvisers()}
    </>
  );
});
